<template>
  <div class="projet-part">
    <h2 id="conseil">{{ $t("projetPage.mon-annee.conseils.titre") }}</h2>

    <!-- WARNING : it's a vee-validate hack On cache l'input mais on garde juste son message si pas de conseil-->
    <div
      v-if="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT_LIGHT' &&
        (!projetAnnuel.conseils || projetAnnuel.conseils.length == 0)
      "
      class="vee-validate-hack"
    >
      <InputWithValidation
        :validation-activated="validationActivated"
        vid="conseil_number"
        rules="required"
      />
      <b-message v-if="validationActivated" type="is-danger"
        >{{ $t("projetPage.mon-annee.conseils.conseils-missing") }}
      </b-message>
    </div>
    <div class="conseil-cards">
      <ConseilCard
        v-if="!readonly"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        class="mr-3"
        :projet-annuel="projetAnnuel"
        :can-delete="false"
        :readonly="readonly"
        @add="added"
      />

      <div v-for="conseil in projetAnnuel.conseils" :key="conseil.id" class="mr-3">
        <ConseilCard
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :conseil="conseil"
          :projet-annuel="projetAnnuel"
          :can-delete="!readonly"
          :readonly="true"
          @delete="deleted"
        />
      </div>
    </div>

    <div class="mt-5">
      <SelectWithValidation
        :model-value="projetAnnuel.constitutionConseilTerreMer"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
        :disabled="readonly || !isPorteurProjet"
        vid="conseil_constitutionConseilTerreMer"
        :data="constitutionConseil"
        :select-labels="constitutionConseilLabels"
        :label="$t('projetPage.mon-annee.conseils.constitution')"
        :input-notice="$t('projetPage.mon-annee.conseils.constitution-message')"
        @update:model-value="updateConstitutionconseilterremer"
        @manual-change="changeConseils"
      />
      <InputWithValidation
        v-if="displayOther"
        :model-value="projetAnnuel.constitutionConseilTerreMerAutre"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
        :label="$t('projetPage.mon-annee.conseils.constitution-autre')"
        :placeholder="$t(`projetPage.mon-annee.conseils.constitution-autre`)"
        :disabled="readonly || !isPorteurProjet"
        vid="conseil_autre"
        @update:model-value="updateConstitutionconseilterremerautre"
        @manual-change="changeConseils"
      />
      <InputWithValidation
        :model-value="projetAnnuel.projetAbordeDebutAnnee"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
        type="textarea"
        :label="$t('projetPage.mon-annee.autres-infos.projet-aborde-debut-annee')"
        :input-notice="$t('projetPage.mon-annee.autres-infos.projet-aborde-debut-annee-hint')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="conseil_projetAbordeDebutAnnee"
        @update:model-value="updateProjetabordedebutannee"
        @manual-change="changeConseils"
      />
      <InputWithValidation
        v-if="displayOrganisation"
        :model-value="projetAnnuel.organisationClasses"
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.conseils.organisation')"
        maxlength="1400"
        :disabled="readonly || !isPorteurProjet"
        vid="conseil_organisationClasses"
        @update:model-value="updateOrganisationclasses"
        @manual-change="changeConseils"
      />
      <b-message
        v-if="
          validationActivated &&
          (projetAnnuel.typeDossier === 'DOSSIER_LABELLISATION' ||
            projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT') &&
          showRequiredMessage
        "
        type="is-danger"
      >
        {{ $t("projetPage.mon-annee.autres-infos.labellisation-renouvellement") }}
      </b-message>
      <InputWithValidation
        :validation-activated="validationActivated"
        :show-validation-errors="showValidationErrors"
        type="textarea"
        :label="$t('projetPage.mon-annee.conseils.liens')"
        :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : calculatedRules"
        maxlength="1400"
        :model-value="projetAnnuel.conseilsLiens"
        :disabled="readonly || !isPorteurProjet"
        vid="conseil_conseilsLiens"
        @update:model-value="updateConseilsliens"
        @manual-change="changeConseilsLiens"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { ConseilDTO, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { defineComponent } from "vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import SelectWithValidation from "@/views/inputs/SelectWithValidation.vue"
import ConseilCard from "@/views/project/ConseilCard.vue"
import { ConseilService } from "@/services/rest/project/ConseilService"
import { SettingsService } from "@/services/rest/utils/SettingsService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { BMessage } from "buefy"

export default defineComponent({
  name: "ConseilsDesElevesView",
  components: {
    SelectWithValidation,
    InputWithValidation,
    ConseilCard,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "dirtify-project", "trigger-validation"],
  data() {
    return {
      conseilService: ConseilService.INSTANCE,
      settingsService: SettingsService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      maxUploadSizeString: "",
      displayOther: false,
      displayOrganisation: false,
      constitutionConseil: [
        "CLASSES_ENTIERES",
        "DELEGUES",
        "CLASSES_ENTIERES_PUIS_DELEGUES",
        "GROUPE_ELEVES",
        "GROUPE_ELEVES_ET_DELEGUES",
        "AUTRE",
      ],
      calculatedRules: "required",
      showRequiredMessage: true,
    }
  },
  computed: {
    constitutionConseilLabels(): string[] {
      return this.constitutionConseil.map((value) => {
        return this.$t(`projetPage.mon-annee.conseils.${value}`).toString()
      })
    },
  },
  watch: {
    projetAnnuel: {
      handler(): void {
        this.projetAnnuelChanged()
      },
      deep: true,
    },
    "projetAnnuel.classes": {
      handler(): void {
        this.classesChanged()
      },
    },
    "projetAnnuel.problematiquesLiens": {
      handler(): void {
        this.updateConseilsLiensRules()
      },
    },
    "projetAnnuel.diffusionLiensEnseignements": {
      handler(): void {
        this.updateConseilsLiensRules()
      },
    },
    "projetAnnuel.etatDesLieuxLiensEnseignements": {
      handler(): void {
        this.updateConseilsLiensRules()
      },
    },
    "projetAnnuel.conseilsLiens": {
      handler(): void {
        this.updateConseilsLiensRules()
      },
    },
  },
  created() {
    this.computeDisplayOther()
    this.computeDisplayOrganisation()
  },
  mounted() {
    this.updateConseilsLiensRules()
  },
  methods: {
    updateConseilsliens(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        conseilsLiens: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateOrganisationclasses(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        organisationClasses: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateProjetabordedebutannee(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        projetAbordeDebutAnnee: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateConstitutionconseilterremerautre(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        constitutionConseilTerreMerAutre: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    updateConstitutionconseilterremer(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        constitutionConseilTerreMer: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
    },
    projetAnnuelChanged(): void {
      if (!this.projetAnnuel || !this.projetAnnuel.id) {
        return
      }
      this.computeDisplayOther()
      this.computeDisplayOrganisation()
    },

    classesChanged(): void {
      this.computeDisplayOrganisation()
    },

    updateConseilsLiensRules(): void {
      if (
        !this.projetAnnuel.problematiquesLiens &&
        !this.projetAnnuel.diffusionLiensEnseignements &&
        !this.projetAnnuel.etatDesLieuxLiensEnseignements
      ) {
        this.calculatedRules = "required"
      } else {
        if (this.projetAnnuel.conseilsLiens) {
          this.calculatedRules = "required"
        } else {
          this.calculatedRules = ""
        }
      }

      this.showRequiredMessage = !(
        this.projetAnnuel.problematiquesLiens ||
        this.projetAnnuel.conseilsLiens ||
        this.projetAnnuel.diffusionLiensEnseignements ||
        this.projetAnnuel.etatDesLieuxLiensEnseignements
      )
    },

    changeConseils(): void {
      this.computeDisplayOther()
      this.$nextTick(() => {
        this.$emit("trigger-validation")
        this.$emit("dirtify-project")
      })
    },

    changeConseilsLiens(): void {
      this.updateConseilsLiensRules()
      this.changeConseils()
    },

    computeDisplayOther(): void {
      this.displayOther = this.projetAnnuel.constitutionConseilTerreMer === "AUTRE"
    },

    computeDisplayOrganisation(): void {
      this.displayOrganisation = !!(
        this.projetAnnuel.classes.length > 1 || this.projetAnnuel.organisationClasses
      )
    },

    added(conseil: ConseilDTO, file: File): void {
      if (this.projetAnnuel.id) {
        this.conseilService
          .create(this.projetAnnuel.id, conseil, file)
          .then((projetAnnuel) => {
            const updatedProjetAnnuel = {
              ...this.projetAnnuel,
              fichiers: projetAnnuel.fichiers,
              conseils: projetAnnuel.conseils,
            }
            this.$emit("projet-annuel-updated", updatedProjetAnnuel)
            this.$nextTick(() => this.$emit("trigger-validation"))
          })
          .catch(async (error) => {
            const settings = await this.settingsService.getSettings()
            const uploadSize = parseInt(settings.MAX_UPLOAD_SIZE.toLowerCase().replace("K", ""))
            if (uploadSize > 0) {
              this.maxUploadSizeString = "(max " + Math.floor(uploadSize / 1000) + "Mo)"
            }
            this.infoReportingService.error(
              this.$t("upload-failed", [this.maxUploadSizeString]).toString(),
              error,
            )
          })
      }
    },

    deleted(conseil: ConseilDTO): void {
      if (this.projetAnnuel.id) {
        this.conseilService.delete(this.projetAnnuel.id, conseil.id).then((projetAnnuel) => {
          const updatedProjetAnnuel = {
            ...this.projetAnnuel,
            fichiers: projetAnnuel.fichiers,
            conseils: projetAnnuel.conseils,
          }
          this.$emit("projet-annuel-updated", updatedProjetAnnuel)
          this.$nextTick(() => this.$emit("trigger-validation"))
        })
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.conseil-cards {
  display: flex;
  flex-wrap: wrap;
  height: auto;
}
</style>
